import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { usePageSnackbar } from "../common/usePageSnackbar";

// Backend returns `sesProvisioning` on POST /api/domains describing whether it
// was able to auto-create the SES identity via the v2 API. We surface the outcome
// in the success snackbar so users know whether they still need to do anything.
function buildAddDomainSuccessMessage(payload, label) {
  const ses = payload?.sesProvisioning;
  if (!ses) return `${label} added successfully.`;

  if (ses.ok) {
    const cnameCount = Array.isArray(ses.dkimCnames)
      ? ses.dkimCnames.length
      : 0;
    const reused = ses.existed ? " (existing SES identity reused)" : "";
    return `${label} added. SES identity provisioned${reused}. Publish ${cnameCount} DKIM CNAME(s) shown in "View Details".`;
  }

  if (ses.error === "SESApiNotConfigured") {
    return `${label} added locally. AWS credentials are not configured on the server — an admin must add the domain in the SES console manually.`;
  }

  return `${label} added locally, but SES identity creation failed: ${ses.message || ses.error || "unknown error"}.`;
}

export function useDomains() {
  const [domain, setDomain] = useState("");
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { setSuccessMessage, setErrorMessage, snackbarProps } =
    usePageSnackbar();
  const [refreshingRatings, setRefreshingRatings] = useState({});
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [checkingDomains, setCheckingDomains] = useState({});
  const [flushingCache, setFlushingCache] = useState(false);

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [pendingDomain, setPendingDomain] = useState("");
  const [domainPassword, setDomainPassword] = useState("");

  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [currentRecordType, setCurrentRecordType] = useState(null);

  const handleOpenInfoDialog = (recordType) => {
    setCurrentRecordType(recordType);
    setInfoDialogOpen(true);
  };

  const handleCloseInfoDialog = () => {
    setInfoDialogOpen(false);
    setCurrentRecordType(null);
  };

  const fetchDomains = useCallback(async () => {
    try {
      const [domainsResponse, emailsResponse] = await Promise.all([
        axios.get("/api/domains"),
        axios.get("/api/emails"),
      ]);

      console.log("Response from /api/domains:", domainsResponse.data);

      if (Array.isArray(domainsResponse.data)) {
        const rawDomains = domainsResponse.data;
        const emails = emailsResponse.data || [];

        const domainsWithBimiStatus = rawDomains.map((d) => {
          const domainEmails = emails.filter(
            (email) =>
              email.email.split("@")[1].toLowerCase() ===
              d.domain.toLowerCase(),
          );
          const hasBimiEmail = domainEmails.some(
            (email) => email.bimiStatus === true,
          );

          return {
            ...d,
            bimiStatus: hasBimiEmail || d.bimiStatus,
            domainEmails,
          };
        });

        setDomains(domainsWithBimiStatus);
      } else {
        console.error("Response data is not an array:", domainsResponse.data);
        setDomains([]);
      }
    } catch (error) {
      console.error("Failed to fetch domains", error);
      setErrorMessage("Failed to fetch domains.");
    }
  }, [setErrorMessage]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  useEffect(() => {
    const handleDomainsUpdated = () => {
      fetchDomains();
    };
    window.addEventListener("domainsUpdated", handleDomainsUpdated);
    return () => {
      window.removeEventListener("domainsUpdated", handleDomainsUpdated);
    };
  }, [fetchDomains]);

  useEffect(() => {
    console.log("Fetched domains:", domains);
  }, [domains]);

  const handleRefreshDomainRatings = async (domainId) => {
    setRefreshingRatings((prev) => ({ ...prev, [domainId]: true }));
    try {
      await axios.put(`/api/domains/${domainId}/ratings`);
      await fetchDomains();
      setSuccessMessage("Domain ratings updated successfully");
    } catch (error) {
      console.error("Error updating domain ratings:", error);
      setErrorMessage("Error updating domain ratings");
    } finally {
      setRefreshingRatings((prev) => ({ ...prev, [domainId]: false }));
    }
  };

  const handleRefreshAllRatings = async () => {
    setRefreshingAll(true);
    try {
      await axios.post("/api/emails/update-all-ratings");
      await fetchDomains();
      setSuccessMessage("All ratings updated successfully");
    } catch (error) {
      console.error("Error updating all ratings:", error);
      setErrorMessage("Error updating all ratings");
    } finally {
      setRefreshingAll(false);
    }
  };

  const handleAddDomainWithPassword = async (domainToAdd, password) => {
    try {
      const response = await axios.post("/api/domains", {
        domain: domainToAdd,
        domainPassword: password,
      });
      setDomains((prev) => [...prev, response.data]);
      setDomain("");
      setDomainPassword("");
      setPendingDomain("");
      setSuccessMessage(
        buildAddDomainSuccessMessage(response.data, "Protected domain"),
      );
    } catch (error) {
      console.error("Failed to add protected domain", error);
      setErrorMessage(
        error.response?.data?.error || "Failed to add protected domain",
      );

      if (
        error.response?.status === 403 &&
        error.response?.data?.error === "Domain access blocked"
      ) {
        setErrorMessage(
          "Your account has been blocked from adding domains due to multiple unauthorized attempts.",
        );
      }
    }
  };

  const handleAddDomain = async () => {
    try {
      if (!domain) {
        setErrorMessage("Please enter a domain.");
        return;
      }
      const response = await axios.post("/api/domains", {
        domain,
        domainPassword,
      });
      setDomains((prev) => [...prev, response.data]);
      setDomain("");
      setDomainPassword("");
      setSuccessMessage(buildAddDomainSuccessMessage(response.data, "Domain"));
    } catch (error) {
      console.error("Failed to add domain", error);

      if (error.response?.data?.isProtectedDomain) {
        setPendingDomain(domain);
        setPasswordDialogOpen(true);
        return;
      }

      if (
        error.response?.status === 403 &&
        error.response?.data?.error === "Domain access blocked"
      ) {
        setErrorMessage(
          "Your account has been blocked from adding domains due to multiple unauthorized attempts.",
        );
        return;
      }

      if (error.response && error.response.data && error.response.data.error) {
        setErrorMessage(`Error: ${error.response.data.error}`);
      } else {
        setErrorMessage("Failed to add domain. Please try again later.");
      }
    }
  };

  const handlePasswordSuccess = (password) => {
    setDomainPassword(password);
    setPasswordDialogOpen(false);

    setTimeout(() => {
      handleAddDomainWithPassword(pendingDomain, password);
    }, 100);
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPendingDomain("");
    setDomainPassword("");
  };

  const handleOpenDialog = (d) => {
    setSelectedDomain(d);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedDomain(null);
  };

  const handleOpenConfirmDialog = (d) => {
    setSelectedDomain(d);
    setConfirmOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmOpen(false);
    setSelectedDomain(null);
  };

  const handleDeleteDomain = async () => {
    try {
      await axios.delete(`/api/domains/${selectedDomain._id}`);
      setDomains((prev) => prev.filter((d) => d._id !== selectedDomain._id));
      setConfirmOpen(false);
      setSelectedDomain(null);
      setSuccessMessage("Domain deleted successfully.");
    } catch (error) {
      console.error("Failed to delete domain", error);
      if (error.response && error.response.data && error.response.data.error) {
        setErrorMessage(`Error: ${error.response.data.error}`);
      } else {
        setErrorMessage("Failed to delete domain. Please try again later.");
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setSuccessMessage("Copied to clipboard!");
      },
      (err) => {
        console.error("Failed to copy: ", err);
        setErrorMessage("Failed to copy text.");
      },
    );
  };

  const handleCheckAllRecords = async (domainName) => {
    try {
      setCheckingDomains((prev) => ({ ...prev, [domainName]: true }));
      const response = await axios.post("/api/domains/check-all", {
        domain: domainName,
      });

      setDomains((prev) =>
        prev.map((d) => {
          if (d.domain === domainName) {
            return {
              ...d,
              spfStatus: response.data.results.spf.status,
              dkimStatus: response.data.results.dkim.status,
              dmarcStatus: response.data.results.dmarc.status,
              bimiRecord: response.data.results.bimi.record || d.bimiRecord,
            };
          }
          return d;
        }),
      );

      await fetchDomains();

      const dkimResult = response.data.results.dkim;
      if (
        !dkimResult.status &&
        dkimResult.errorType === "SES_DKIM_NOT_CONFIGURED"
      ) {
        setErrorMessage(
          `⚠️ DKIM not configured for ${domainName}. We don't have the three SES CNAMEs stored locally yet — open "View Details" and re-provision the domain, or have an admin check AWS_ACCESS_KEY_ID / AWS_SECRET_ACCESS_KEY.`,
        );
      } else if (!dkimResult.status) {
        setErrorMessage(
          `⚠️ DKIM check failed for ${domainName}: ${dkimResult.message || "one or more SES CNAMEs are missing in DNS."} Open "View Details" to see the exact records to publish.`,
        );
      } else if (response.status === 200) {
        setSuccessMessage(`All DNS records for ${domainName} are valid.`);
      } else {
        const totalChecks = 4;
        const successfulChecks = [
          response.data.results.spf.status,
          response.data.results.dkim.status,
          response.data.results.dmarc.status,
          response.data.results.bimi.status,
        ].filter(Boolean).length;

        setSuccessMessage(
          `DNS records checked for ${domainName}. ${successfulChecks}/${totalChecks} records are valid. Check details for more information.`,
        );
      }
    } catch (error) {
      console.error(
        `Failed to check DNS records for domain ${domainName}`,
        error,
      );
      setErrorMessage(`Failed to check DNS records for ${domainName}.`);
    } finally {
      setCheckingDomains((prev) => ({ ...prev, [domainName]: false }));
    }
  };

  const handleFlushDnsCache = async () => {
    try {
      setFlushingCache(true);
      await axios.post("/api/domains/flush-cache");
      setSuccessMessage("DNS resolver cache cleared. Re-check records now.");
    } catch (error) {
      console.error("Failed to flush DNS cache", error);
      setErrorMessage("Failed to flush DNS cache.");
    } finally {
      setFlushingCache(false);
    }
  };

  return {
    domain,
    setDomain,
    domains,
    selectedDomain,
    detailsOpen: open,
    confirmOpen,
    snackbarProps,
    refreshingRatings,
    refreshingAll,
    checkingDomains,
    flushingCache,
    passwordDialogOpen,
    pendingDomain,
    handleRefreshDomainRatings,
    handleRefreshAllRatings,
    handleAddDomain,
    handlePasswordSuccess,
    handlePasswordDialogClose,
    handleOpenDialog,
    handleCloseDialog,
    handleOpenConfirmDialog,
    handleCloseConfirmDialog,
    handleDeleteDomain,
    copyToClipboard,
    handleCheckAllRecords,
    handleFlushDnsCache,
    infoDialogOpen,
    currentRecordType,
    handleOpenInfoDialog,
    handleCloseInfoDialog,
  };
}
